import {css, cx as addStyles, keyframes} from '@emotion/css';
import {Button, MediaMatch, MenuIcon, theme} from '@remoov/remoov-components';
import React, {AnchorHTMLAttributes, HTMLAttributes, ImgHTMLAttributes, useState} from 'react';
import remoovLogo from './logoblue.svg';
import logout from './logout.svg';

export type MenuProps = {
  dashboardLink: () => void | Promise<void>;
  myItemsLink: () => void | Promise<void>;
  newPickupLink: () => void | Promise<void>;
  logoutLink: () => void | Promise<void>;
};

export const Menu = ({dashboardLink, myItemsLink, newPickupLink, logoutLink}: MenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <Container>
      <SubContainer>
        <MediaMatch lessThan='medium'>
          <MenuIconWrapper onClick={() => setIsOpen(!isOpen)}>
            <MenuIcon size='large' />
          </MenuIconWrapper>
        </MediaMatch>
        <Logo src={remoovLogo} alt='remoov logo' />
        <NavigationMenu>
          <MediaMatch greaterThan='medium'>
            <MenuLink onClick={dashboardLink}>Dashboard</MenuLink>
            <MenuLink onClick={myItemsLink}>My items</MenuLink>
          </MediaMatch>
          <Button
            buttonStyle='secondary'
            as='a'
            onClick={newPickupLink}
            className={navigationMenuButtonAditionalStyle}
          >
            New Pickup
          </Button>
          <MediaMatch greaterThan='medium'>
            <Logout onClick={logoutLink}>
              <img src={logout} alt='logout icon' />
            </Logout>
          </MediaMatch>
        </NavigationMenu>
        <MediaMatch lessThan='medium'>
          <FullMenu aria-hidden={!isOpen} isOpen={isOpen}>
            <NavigationMenu horizontal>
              <MenuLink
                onClick={dashboardLink}
                className={addStyles(menuLinkStyleForFullMenu, translateYTransition(isOpen))}
              >
                Dashboard
              </MenuLink>
              <MenuLink
                onClick={myItemsLink}
                className={addStyles(menuLinkStyleForFullMenu, translateYTransition(isOpen))}
              >
                My items
              </MenuLink>
            </NavigationMenu>
            <Button
              as='a'
              buttonStyle='primary'
              className={translateYTransition(isOpen)}
              onClick={logoutLink}
            >
              Log out
            </Button>
          </FullMenu>
        </MediaMatch>
      </SubContainer>
    </Container>
  );
};
const Container = (args: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={css({
      background: theme.colors.white,
      width: '100%',
      height: '160px',
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
    })}
    {...args}
  />
);
const SubContainer = (args: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={css({
      width: '100%',
      maxWidth: '1200px',
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexWrap: 'nowrap',
      position: 'relative',
      padding: `0 ${theme.spacings.xxsmall}`,
      svg: {cursor: 'pointer'},
      [theme.mediaMaxWidth.small]: {padding: `0 ${theme.spacings.xsmall}`},
    })}
    {...args}
  />
);
const Logo = (args: ImgHTMLAttributes<HTMLImageElement>) => (
  <img
    className={css({
      margin: '0',
      height: '32px',
      [theme.mediaMaxWidth.medium]: {position: 'absolute', left: theme.spacings.xlarge},
      '@media (max-width:500px)': {height: '20px', marginLeft: '8px'},
    })}
    {...args}
  />
);
const MenuIconWrapper = (args: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={css({
      width: '32px',
      height: '32px',
      cursor: 'pointer',
      color: theme.colors.darkBlue,
    })}
    {...args}
  />
);
const NavigationMenu = ({
  horizontal,
  ...rest
}: HTMLAttributes<HTMLDivElement> & {horizontal?: boolean}) => (
  <div
    className={
      horizontal
        ? css({
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'flex-start',
          })
        : css({
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
          })
    }
    {...rest}
  />
);
const navigationMenuButtonAditionalStyle = css({
  textDecoration: 'none',
  [theme.mediaMaxWidth.medium]: {position: 'absolute', right: theme.spacings.xsmall},
  '@media (max-width: 500px)': {marginLeft: '88px', width: '116px', height: '38px'},
});
const hoverAnimation = keyframes({'0%': {width: 0, left: '50%'}, '100%': {width: '100%', left: 0}});
const MenuLink = ({className, ...rest}: AnchorHTMLAttributes<HTMLAnchorElement>) => (
  <a
    className={addStyles(
      css({
        fontSize: theme.font.sizes.small,
        color: theme.colors.darkBlue,
        marginRight: theme.spacings.large,
        fontFamily: theme.font.family,
        fontWeight: theme.font.medium,
        textDecoration: 'none',
        whiteSpace: 'nowrap',
        cursor: 'pointer',
        [theme.mediaMaxWidth.medium]: {
          '&:hover': {
            '&::after': {
              content: "''",
              position: 'absolute',
              display: 'block',
              height: '3px',
              backgroundColor: theme.colors.orange,
              animation: `${hoverAnimation} 0.2s forwards`,
            },
          },
        },
      }),
      className,
    )}
    {...rest}
  />
);
const Logout = (args: AnchorHTMLAttributes<HTMLAnchorElement>) => (
  <a
    className={css({
      background: 'transparent',
      border: '0',
      outline: 'none',
      cursor: 'pointer',
      width: '40px',
      height: '40px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyLontent: 'center',
      marginLeft: theme.spacings.large,
    })}
    {...args}
  />
);
const FullMenu = ({isOpen, ...rest}: {isOpen: boolean} & HTMLAttributes<HTMLElement>) => (
  <nav
    className={css({
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      width: '100%',
      padding: `0 ${theme.spacings.small}`,
      background: theme.colors.lightBlue,
      borderBottom: `solid 1px ${theme.colors.darkBlue}`,
      position: 'absolute',
      top: '44px',
      left: '0',
      height: '200px',
      zIndex: 999,
      opacity: isOpen ? 1 : 0,
      transition: 'opacity 0.3s ease-in-out',
      pointerEvents: isOpen ? 'all' : 'none',
    })}
    {...rest}
  />
);
const menuLinkStyleForFullMenu = css({
  fontSize: theme.font.sizes.small,
  fontWeight: theme.font.medium,
  marginRight: 0,
  marginBottom: theme.spacings.small,
});
const translateYTransition = (isOpen: boolean) =>
  css({
    transition: 'transform 0.3s ease-in-out',
    transform: isOpen ? 'translateY(30px)' : 'translateY(0)',
  });
